import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { formatCurrency, formatPercent } from '@/lib/utils';
import { LineItemData, type ProjectData } from './LineItemList';
import { BUILT_IN_KEYS } from './subItemTypes';
import { getCostBreakdown, type Rates, DEFAULT_TAX_RATE, DEFAULT_MARKUP_RATE } from '@jobos/shared';
import { BreakdownTableRow } from './BreakdownTableRow';
import { TriStateCheckbox } from '@/components/ui/tri-state-checkbox';
import { useSubItemCategoriesContext, useSubItemTypeMeta } from '@/contexts/SubItemCategoriesContext';

interface BreakdownTableViewProps {
  lineItems: LineItemData[];
  projects?: ProjectData[];
  onLineItemsChange: (items: LineItemData[]) => void;
  onDeleteLineItem: (id: string) => void;
  readOnly?: boolean;
  taxRate?: number;
  markupRate?: number;
  checkedIds?: Set<string>;
  onCheckItem?: (id: string, checked: boolean) => void;
  selectAllChecked?: boolean;
  selectAllIndeterminate?: boolean;
  selectAllDisabled?: boolean;
  onSelectAll?: () => void;
  expandedIds?: Set<string>;
  onToggleExpand?: (id: string) => void;
}

export function BreakdownTableView({
  lineItems,
  projects = [],
  onLineItemsChange,
  onDeleteLineItem,
  readOnly = false,
  taxRate = DEFAULT_TAX_RATE,
  markupRate = DEFAULT_MARKUP_RATE,
  checkedIds,
  onCheckItem,
  selectAllChecked,
  selectAllIndeterminate,
  selectAllDisabled,
  onSelectAll,
  expandedIds,
  onToggleExpand,
}: BreakdownTableViewProps) {
  const { categories } = useSubItemCategoriesContext();
  const typeMeta = useSubItemTypeMeta();
  const rates: Rates = { taxRate, markupRate };
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    if (readOnly) return;
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (readOnly) return;
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = lineItems.findIndex((item) => item.id === active.id);
      const newIndex = lineItems.findIndex((item) => item.id === over.id);
      onLineItemsChange(arrayMove(lineItems, oldIndex, newIndex));
    }
  };

  const activeItem = activeId ? lineItems.find((item) => item.id === activeId) : null;

  const handleUpdateItem = (id: string, updates: Partial<LineItemData>) => {
    onLineItemsChange(
      lineItems.map((item) => {
        if (item.id !== id) return item;
        const newItem = { ...item, ...updates };
        // Recalculate total if subItems changed
        if (updates.subItems) {
          const breakdown = getCostBreakdown(updates.subItems, rates, typeMeta, categories);
          newItem.totalCost = breakdown.total;
        }
        return newItem;
      })
    );
  };

  // Categories excluding profit/discount for column headers
  const dataCols = categories.filter(
    (cat) => cat.key !== BUILT_IN_KEYS.PROFIT && cat.key !== BUILT_IN_KEYS.DISCOUNT
  );
  // All items including project items for totals
  const allItems = [
    ...lineItems,
    ...projects.flatMap((p) => p.lineItems),
  ];

  // Calculate grand totals dynamically
  const grandTotals = allItems.reduce(
    (acc, item) => {
      const breakdown = getCostBreakdown(item.subItems, rates, typeMeta, categories);
      const newAcc = { ...acc };
      for (const key of Object.keys(breakdown.categoryTotals)) {
        newAcc.categoryTotals[key] = (newAcc.categoryTotals[key] || 0) + (breakdown.categoryTotals[key] ?? 0);
      }
      newAcc.tax += breakdown.tax;
      newAcc.markup += breakdown.markup;
      newAcc.jobMarkup += breakdown.jobMarkup;
      newAcc.customMarkup += breakdown.customMarkup;
      newAcc.total += breakdown.total;
      return newAcc;
    },
    {
      categoryTotals: {} as Record<string, number>,
      tax: 0,
      markup: 0,
      jobMarkup: 0,
      customMarkup: 0,
      total: 0,
    }
  );

  if (lineItems.length === 0 && projects.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No line items yet. Add items using the templates or the + button above.
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div
        className="overflow-auto [&_input:focus]:!ring-2 [&_input:focus]:!ring-inset [&_input:focus]:!ring-blue-500 [&_input:focus]:!outline-none [&_textarea:focus]:!ring-2 [&_textarea:focus]:!ring-inset [&_textarea:focus]:!ring-blue-500 [&_textarea:focus]:!outline-none [&_[role=combobox]:focus]:!ring-2 [&_[role=combobox]:focus]:!ring-blue-500 [&_[role=combobox]:focus]:!outline-none"
        style={{ maxHeight: 'calc(100vh - 16rem)' }}
      >
        <table className="w-full text-left">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-gray-300 bg-gray-50 shadow-sm">
              <th className="w-8 px-1 text-center">
                {onSelectAll && (
                  <TriStateCheckbox
                    checked={selectAllChecked ?? false}
                    indeterminate={selectAllIndeterminate ?? false}
                    disabled={selectAllDisabled ?? false}
                    onChange={onSelectAll}
                    title="Select all"
                  />
                )}
              </th>
              <th className="py-2 pl-10 pr-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Category
              </th>
              <th className="py-2 px-2 text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-48">
                Description
              </th>
              {dataCols.map((cat) => (
                <th key={cat.key} className="py-2 px-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {cat.label}
                </th>
              ))}
              <th className="py-2 px-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Profit
              </th>
              <th className="py-2 px-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Discount
              </th>
              <th className="py-2 pl-2 pr-10 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">
                Total
              </th>
            </tr>
          </thead>
          <SortableContext items={lineItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            <tbody>
              {projects.map((project) => {
                const projectTotal = project.lineItems.reduce((sum, item) => {
                  const breakdown = getCostBreakdown(item.subItems, rates, typeMeta, categories);
                  return sum + breakdown.total;
                }, 0);
                return (
                  <tr key={`project-header-${project.id}`}>
                    <td colSpan={dataCols.length + 6} className="py-2 px-3 bg-muted/50 border-y border-border">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-foreground">{project.name}</span>
                        <span className="text-xs font-medium text-muted-foreground tabular-nums">
                          {formatCurrency(projectTotal)}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(() => {
                let itemNumber = 1;
                const projectRows = projects.flatMap((project) =>
                  project.lineItems.map((item) => (
                    <BreakdownTableRow
                      key={item.id}
                      item={item}
                      itemNumber={itemNumber++}
                      onUpdate={(updates) => handleUpdateItem(item.id, updates)}
                      onDelete={() => onDeleteLineItem(item.id)}
                      readOnly={readOnly}
                      taxRate={taxRate}
                      markupRate={markupRate}
                      checked={checkedIds?.has(item.id)}
                      onCheck={onCheckItem}
                      isExpanded={expandedIds?.has(item.id) ?? false}
                      onToggleExpand={onToggleExpand}
                    />
                  ))
                );
                const separator = lineItems.length > 0 && projects.length > 0 ? (
                  <tr key="ungrouped-separator">
                    <td colSpan={dataCols.length + 6} className="py-1 px-3 bg-muted/30 border-y border-border">
                      <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Ungrouped Items</span>
                    </td>
                  </tr>
                ) : null;
                const looseRows = lineItems.map((item) => (
                  <BreakdownTableRow
                    key={item.id}
                    item={item}
                    itemNumber={itemNumber++}
                    onUpdate={(updates) => handleUpdateItem(item.id, updates)}
                    onDelete={() => onDeleteLineItem(item.id)}
                    readOnly={readOnly}
                    taxRate={taxRate}
                    markupRate={markupRate}
                    checked={checkedIds?.has(item.id)}
                    onCheck={onCheckItem}
                    isExpanded={expandedIds?.has(item.id) ?? false}
                    onToggleExpand={onToggleExpand}
                  />
                ));
                return [...projectRows, separator, ...looseRows];
              })()}
            </tbody>
          </SortableContext>
          <tfoot>
            <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
              <td></td>
              <td className="py-3 pl-10 pr-3 text-sm text-gray-700">Subtotals</td>
              <td></td>
              {dataCols.map((cat) => (
                <td key={cat.key} className="py-3 px-2 text-xs tabular-nums text-right">
                  {formatCurrency(grandTotals.categoryTotals[cat.key] || 0)}
                </td>
              ))}
              <td className="py-3 px-2 text-xs tabular-nums text-right">
                {formatCurrency(grandTotals.categoryTotals[BUILT_IN_KEYS.PROFIT] || 0)}
              </td>
              <td className="py-3 px-2 text-xs tabular-nums text-right">
                {(grandTotals.categoryTotals[BUILT_IN_KEYS.DISCOUNT] || 0) > 0
                  ? `-${formatCurrency(grandTotals.categoryTotals[BUILT_IN_KEYS.DISCOUNT] || 0)}`
                  : formatCurrency(grandTotals.categoryTotals[BUILT_IN_KEYS.DISCOUNT] || 0)}
              </td>
              <td></td>
            </tr>
            <tr className="bg-gray-50">
              <td colSpan={dataCols.length + 3}></td>
              <td colSpan={2} className="py-2 px-2 text-xs text-gray-600 text-right">
                Tax ({formatPercent(taxRate)} on labor):
              </td>
              <td className="py-2 pl-2 pr-10 text-xs tabular-nums text-right">
                {formatCurrency(grandTotals.tax)}
              </td>
            </tr>
            {grandTotals.jobMarkup > 0 && (
              <tr className="bg-gray-50">
                <td colSpan={dataCols.length + 3}></td>
                <td colSpan={2} className="py-2 px-2 text-xs text-gray-600 text-right">
                  Markup ({formatPercent(markupRate)}):
                </td>
                <td className="py-2 pl-2 pr-10 text-xs tabular-nums text-right">
                  {formatCurrency(grandTotals.jobMarkup)}
                </td>
              </tr>
            )}
            {grandTotals.customMarkup > 0 && (
              <tr className="bg-gray-50">
                <td colSpan={dataCols.length + 3}></td>
                <td colSpan={2} className="py-2 px-2 text-xs text-gray-600 text-right">
                  Custom markup *:
                </td>
                <td className="py-2 pl-2 pr-10 text-xs tabular-nums text-right">
                  {formatCurrency(grandTotals.customMarkup)}
                </td>
              </tr>
            )}
            <tr className="bg-gray-50 font-semibold border-t border-gray-300">
              <td colSpan={dataCols.length + 3}></td>
              <td colSpan={2} className="py-3 px-2 text-sm text-gray-700 text-right">
                Total:
              </td>
              <td className="py-3 pl-2 pr-10 text-sm tabular-nums text-right text-gray-900">
                {formatCurrency(grandTotals.total)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <DragOverlay>
        {activeItem ? (
          <div className="bg-blue-50 border border-blue-200 px-3 py-2 shadow-lg rounded opacity-90">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">{activeItem.category}</span>
              <span className="text-xs text-gray-500 truncate max-w-[200px]">{activeItem.description}</span>
              <span className="text-sm font-semibold text-gray-900 ml-auto">
                {formatCurrency(activeItem.totalCost)}
              </span>
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
